import './index.css';

import gamestackTexture2Placeholder from 'assets/gamestack-list-placeholder.jpg';
import gamestackTexture2 from 'customAssets/wmlApp1.jpg';
import gamestackTexturePlaceholder from 'assets/gamestack-login-placeholder.jpg';
import gamestackTexture from 'customAssets/wmlApp2.jpg';
import twickImg1 from 'customAssets/twick1.jpg';
import twickImg2 from 'customAssets/twick2.jpg';
import iphone11 from 'assets/iphone-11.glb';
import macbookPro from 'assets/macbook-pro.glb';
import sprTexturePlaceholder from 'assets/spr-lesson-builder-dark-placeholder.jpg';
import pickAClass from 'assets/pac/pickaclass.png';

import sprTexture from 'customAssets/wmlWebsitePreview.jpg';
import fuddle1 from 'assets/fuddle/fuddle1.png';
import fuddle2 from 'assets/fuddle/fuddle2.png';
import uni1 from 'assets/unimaps/uni1.png';
import uni2 from 'assets/unimaps/uni2.png';
import { usePrefersReducedMotion, useRouteTransition } from 'hooks';
import Intro from 'pages/Home/Intro';
import Profile from 'pages/Home/Profile';
import ProjectSummary from 'pages/Home/ProjectSummary';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { Clients } from './clientsSection/Clients';
import DecoderText from 'components/DecoderText';
import { ProductionStrategy } from './productionStrategy/productionStrategy';
import { EndFooter } from './footer/footer';
import { PartnersSection } from './partnersSection';

const disciplines = [`Developer`, `Entrepreneur`];
export const appJoxDescription =
  "Hi, I'm Aryash, a seasoned full stack developer with a passion for creating cutting-edge applications and platforms. I started my coding journey at the age of 14, and since then, I've gained extensive experience in multiple domains, including mobile and web development, backend development, and artificial intelligence.";
export const appJoxTitle = 'Aryash Dubey | Developer';

const Home = () => {
  const { status } = useRouteTransition();
  const { hash, state } = useLocation();
  const initHash = useRef(true);
  const [visibleSections, setVisibleSections] = useState([]);
  const [scrollIndicatorHidden, setScrollIndicatorHidden] = useState(false);
  const intro = useRef();
  const projectOne = useRef();
  const projectTwo = useRef();
  const projectThree = useRef();
  const projectFour = useRef();
  const projectFive = useRef();
  const details = useRef();
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const revealSections = [
      intro,
      projectOne,
      projectTwo,
      projectThree,
      projectFour,
      projectFive,
      details,
    ];

    const sectionObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const section = entry.target;
            observer.unobserve(section);
            if (visibleSections.includes(section)) return;
            setVisibleSections(prevSections => [...prevSections, section]);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );

    const indicatorObserver = new IntersectionObserver(
      ([entry]) => {
        setScrollIndicatorHidden(!entry.isIntersecting);
      },
      { rootMargin: '-100% 0px 0px 0px' }
    );

    revealSections.forEach(section => {
      sectionObserver.observe(section.current);
    });

    indicatorObserver.observe(intro.current);

    return () => {
      sectionObserver.disconnect();
      indicatorObserver.disconnect();
    };
  }, [visibleSections]);

  useEffect(() => {
    const hasEntered = status === 'entered';
    const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;
    let scrollObserver;
    let scrollTimeout;

    const handleHashchange = (hash, scroll) => {
      clearTimeout(scrollTimeout);
      const hashSections = [intro, projectOne, details];
      const hashString = hash.replace('#', '');
      const element = hashSections.filter(item => item.current.id === hashString)[0];
      if (!element) return;
      const behavior = scroll && !prefersReducedMotion ? 'smooth' : 'instant';
      const top = element.current.offsetTop;

      scrollObserver = new IntersectionObserver(
        (entries, observer) => {
          const [entry] = entries;
          if (entry.isIntersecting) {
            scrollTimeout = setTimeout(
              () => {
                element.current.focus();
              },
              prefersReducedMotion ? 0 : 400
            );
            observer.unobserve(entry.target);
          }
        },
        { rootMargin: '-20% 0px -20% 0px' }
      );

      scrollObserver.observe(element.current);

      if (supportsNativeSmoothScroll) {
        window.scroll({
          top,
          left: 0,
          behavior,
        });
      } else {
        window.scrollTo(0, top);
      }
    };

    if (hash && initHash.current && hasEntered) {
      handleHashchange(hash, false);
      initHash.current = false;
    } else if (!hash && initHash.current && hasEntered) {
      window.scrollTo(0, 0);
      initHash.current = false;
    } else if (hasEntered) {
      handleHashchange(hash, true);
    }

    return () => {
      clearTimeout(scrollTimeout);
      if (scrollObserver) {
        scrollObserver.disconnect();
      }
    };
  }, [hash, state, prefersReducedMotion, status]);

  return (
    <div className="home">
      <Helmet>
        <title>{appJoxTitle}</title>
        <meta name="description" content={appJoxDescription} />
        <meta name="og:description" content={appJoxDescription} />
        <meta
          property="og:image"
          content="https://i.ibb.co/WtQsRdm/Screenshot-2022-02-15-204105.png"
        />{' '}
        <meta property="og:title" content={appJoxTitle} />
        <link rel="prefetch" href={iphone11} as="fetch" crossorigin="" />
        <meta name="twitter:description" content={appJoxDescription} />
        <meta name="twitter:title" content={appJoxTitle} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:image"
          content="https://i.ibb.co/WtQsRdm/Screenshot-2022-02-15-204105.png"
        />
        <link rel="prefetch" href={macbookPro} as="fetch" crossorigin="" />
      </Helmet>
      <Intro
        id="intro"
        sectionRef={intro}
        disciplines={disciplines}
        scrollIndicatorHidden={scrollIndicatorHidden}
      />
      <ProjectSummary
        id="project-1"
        alternate
        sectionRef={projectOne}
        visible={visibleSections.includes(projectOne.current)}
        index={1}
        title="One of the best looking Social Media Apps"
        description="This app consists of beautiful designs, smooth animations, and an intuitive user experience."
        buttonText="View App"
        buttonLink="/projects/twick"
        model={{
          type: 'phone',
          alt: 'Twick App',
          textures: [
            {
              src: twickImg1,
              srcSet: { gamestackTexture },
              placeholder: gamestackTexturePlaceholder,
            },
            {
              src: twickImg2,
              srcSet: { gamestackTexture2 },
              placeholder: gamestackTexture2Placeholder,
            },
          ],
        }}
      />{' '}
      <ProjectSummary
        id="project-1"
        sectionRef={projectTwo}
        visible={visibleSections.includes(projectTwo.current)}
        index={2}
        title="Pick A Class"
        description="Pick A Class is the fastest notification app to notify ASU students about class openings."
        buttonText="View Project"
        buttonLink="/projects/pick-a-class"
        model={{
          type: 'laptop',
          alt: 'Pick A Class Website',
          textures: [
            {
              src: sprTexture,
              srcSet: `${pickAClass} 800w, ${pickAClass} 1440w`,
              placeholder: sprTexturePlaceholder,
            },
          ],
        }}
      />
      <ProjectSummary
        id="project-3"
        sectionRef={projectThree}
        visible={visibleSections.includes(projectThree.current)}
        index={3}
        alternate
        title="Because College Events are ❤️"
        description="Unimaps is a groundbreaking startup funded by ASU, aimed at connecting ASU students with various clubs and organizations on campus and making college events an important part of college life."
        buttonText="View App"
        buttonLink="/projects/unimaps"
        model={{
          type: 'phone',
          alt: 'App login screen',
          textures: [
            {
              src: uni2,
              srcSet: { uni2 },
              placeholder: gamestackTexturePlaceholder,
            },
            {
              src: uni1,
              srcSet: { uni1 },
              placeholder: gamestackTexture2Placeholder,
            },
          ],
        }}
      />{' '}
      <ProjectSummary
        id="project-4"
        sectionRef={projectFour}
        visible={visibleSections.includes(projectFour.current)}
        index={4}
        title="Wicked Maine Lobster"
        description="Wicked Maine Lobster is a popular California-based restaurant chain. I lead the development of its food ordering system. "
        buttonText="View Project"
        buttonLink="/projects/wickedMaineLobster"
        model={{
          type: 'laptop',
          alt: 'Wicked Maine Lobster Website',
          textures: [
            {
              src: sprTexture,
              srcSet: `${sprTexture} 800w, ${sprTexture} 1440w`,
              placeholder: sprTexturePlaceholder,
            },
          ],
        }}
      />
      <ProjectSummary
        id="project-4"
        alternate
        sectionRef={projectFive}
        visible={visibleSections.includes(projectFive.current)}
        index={5}
        title="Wanna play Pickup basketball?"
        description="Fuddle is an amazing mobile app designed to unite basketball enthusiasts by helping them discover nearby pickup basketball events."
        buttonText="View App"
        buttonLink="https://apps.apple.com/us/app/fuddle-find-your-huddle/id1624312972?platform=iphone"
        model={{
          type: 'phone',
          alt: 'Fuddle',
          textures: [
            {
              src: fuddle1,
              srcSet: { fuddle1 },
              placeholder: gamestackTexturePlaceholder,
            },
            {
              src: fuddle2,
              srcSet: { fuddle2 },
              placeholder: gamestackTexture2Placeholder,
            },
          ],
        }}
      />{' '}
      <div
        style={{
          justifyContent: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignContent: 'center',
        }}
      >
        {' '}
        <div style={{ height: '64px' }} />
        <DecoderText
          text="Testimonials
          "
          style={{
            justifyContent: 'center',
            textAlign: 'center',
            fontSize: '40px',
            fontWeight: 'bold',
            margin: '0px 16px',
          }}
          start={status !== 'exited'}
          delay={500}
        />{' '}
        <div style={{ height: '32px' }} />
        <h3
          start={status !== 'exited'}
          style={{
            textAlign: 'center',
            margin: '0px 32px',
            justifyContent: 'center',
            alignItems: 'center',
            fontWeight: 'normal',
          }}
          delay={500}
        >
          {' '}
          Testimonials from the people I have worked with.
        </h3>
      </div>
      <Clients
        sectionRef={details}
        visible={visibleSections.includes(details.current)}
        id="details"
      />{' '}
      {/* <PartnersSection /> */}
      <Profile
        sectionRef={details}
        visible={visibleSections.includes(details.current)}
        id="details"
      />
      <EndFooter />
    </div>
  );
};

export default Home;
